import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  NakedForm,
  SubmitButton,
  SwitchField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable, PlayerId } from '@/types';
import {
  PlayerUpdateDepositBlockMutation,
  PlayerUpdateDepositBlockMutationVariables,
} from './__generated__/PlayerUpdateDepositsBlockForm';

const query = graphql`
  query SanityPlayerUpdateDepositsBlockForm {
    sanityPlayerUpdateDepositsBlockForm {
      title {
        ...SanityLocaleString
      }
      description {
        ...SanityLocaleString
      }
      isDepositsBlocked {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const updateCustomerDepositBlockMutation = gql`
  mutation PlayerUpdateDepositBlock($playerGlobalId: ID!, $enable: Boolean!) {
    updateCustomerDepositBlock(
      playerGlobalId: $playerGlobalId
      enable: $enable
    ) {
      ... on Customer {
        id
        isDepositsBlocked
      }
    }
  }
`;

const PlayerUpdateDepositsBlockForm: FC<{
  playerGlobalId: PlayerId;
  isBlocked: boolean;
}> = ({ playerGlobalId, isBlocked }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerUpdateDepositsBlockFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const form = staticData.sanityPlayerUpdateDepositsBlockForm;
  const { close } = useModalContext();
  const { t } = useTranslate();

  const [updateDepositsBlockState, updateDepositsBlock] = useMutation<
    PlayerUpdateDepositBlockMutation,
    PlayerUpdateDepositBlockMutationVariables
  >(updateCustomerDepositBlockMutation);

  const defaultValues = {
    isDepositsBlocked: isBlocked,
  };

  const methods = useForm<typeof defaultValues>({ defaultValues });
  const nextValue = methods.watch('isDepositsBlocked');
  const isUnchanged = nextValue === isBlocked;

  const onSubmit = ({ isDepositsBlocked }: typeof defaultValues) => {
    updateDepositsBlock({
      playerGlobalId,
      enable: isDepositsBlocked,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="md"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3">
          <NakedForm
            methods={methods}
            onSubmit={onSubmit}
            className="grid gap-6"
          >
            <div className="space-y-1">
              <div className="block text-gray-600 text-sm dark:text-gray-200">
                {t(form.description)}
              </div>
            </div>
            <SwitchField
              title={t(form.isDepositsBlocked)}
              name="isDepositsBlocked"
              labelPlacement="above"
              className="col-span-full"
              allowUndefinedState={false}
            />
            <ErrorMessage message={errorMessage} />
            <SubmitButton
              value={t(form.submit)}
              disabled={updateDepositsBlockState.fetching || isUnchanged}
            />
          </NakedForm>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerUpdateDepositsBlockForm;
